/**
 * Stack dismiss model - where "close this overlay" goes in browser history.
 *
 * The navigation stack is derived from the URL, but dismissal has to be
 * expressed as a history traversal. `navigate(-1)` is a bet that the previous
 * history entry is what sat beneath the overlay; duplicate pushes (a double
 * tap on a card), forward entries, and replaces all break that bet and turn
 * back into a no-op. So each stack entry records the history index it was
 * opened over, and dismiss travels to that index however far away it is.
 *
 * NORTHSTARS:
 * - Less code, better
 * - React Router is boss - history.state.idx is its own bookkeeping
 * - One job - pure arithmetic, no DOM
 */

export type HistoryNavigationType = "PUSH" | "POP" | "REPLACE";

/** The last location the router rendered, with the history index it had then. */
export type SeenLocation = {
  id: string;
  idx: number | null;
};

interface ResolveStackUnderIdxInput {
  navigationType: HistoryNavigationType;
  /** history.state.idx of the location that just became the stack entry. */
  currentIdx: number | null;
  /** underIdx of the entry currently on top of the stack, if any. */
  topUnderIdx: number | null;
  entryId: string;
  previous: SeenLocation | null;
}

/**
 * The history index beneath a stack entry that just arrived, or null when the
 * entry is the first thing in history (deep link, cold start).
 *
 * Pushes can coalesce before the router renders (a double tap lands two
 * identical entries), so "one below" is only a fallback; the entry beneath is
 * the location the router last saw, when it really sits below this one.
 */
export const resolveStackUnderIdx = ({
  navigationType,
  currentIdx,
  topUnderIdx,
  entryId,
  previous,
}: ResolveStackUnderIdxInput): number | null => {
  if (navigationType === "REPLACE" && topUnderIdx !== null) {
    return topUnderIdx;
  }
  if (currentIdx === null || currentIdx <= 0) {
    return null;
  }
  if (previous && previous.id !== entryId && previous.idx !== null && previous.idx < currentIdx) {
    return previous.idx;
  }
  return currentIdx - 1;
};

interface ResolveStackDismissDeltaInput {
  underIdx: number | null;
  currentIdx: number | null;
}

/**
 * The `navigate(delta)` that returns to the entry beneath the stack, or null
 * when history holds no answer and the caller must fall back to a tab path.
 */
export const resolveStackDismissDelta = ({
  underIdx,
  currentIdx,
}: ResolveStackDismissDeltaInput): number | null => {
  if (underIdx === null || currentIdx === null) {
    return null;
  }
  if (currentIdx <= underIdx) {
    return null;
  }
  return underIdx - currentIdx;
};

/** React Router keeps its own index on history.state; read it, never write it. */
export const readHistoryIdx = (): number | null => {
  if (typeof window === "undefined") return null;
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : null;
};
